import React, { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const NewsPage: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('全て');
  const [selectedArticle, setSelectedArticle] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // SEO Meta tags and JSON-LD for News page
  useEffect(() => {
    // SEO Meta tags for News page
    document.title = 'お知らせ | 株式会社OnYourSide';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Onyoursideの最新情報、コラム、メディア掲載情報をお届けします。');
    }

    // Schema.org JSON-LD for News page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "お知らせ",
      "description": "Onyoursideの最新情報、コラム、メディア掲載情報をお届けします。",
      "url": `${import.meta.env.VITE_SITE_URL}/news`,
      "publisher": {
        "@type": "Organization",
        "name": "株式会社OnYourSide"
      }
    });
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const categories = ['全て', 'コラム', 'メディア掲載', '研修情報', 'イベント', '会社情報'];

  const newsItems = [
    {
      id: 1,
      category: 'コラム',
      title: '令和型マネジメントと昭和型マネジメントの違い',
      excerpt: '時代とともに変化するマネジメントスタイル。令和時代に求められる新しいリーダーシップの形を解説します。',
      date: '2024年3月22日',
      image: 'https://public.readdy.ai/ai/img_res/aef4c7dde3c94e62b7601c0d55846a26.jpg',
      content: `
        <h3>昭和型マネジメントの特徴</h3>
        <p>昭和型マネジメントは、高度経済成長期に確立された管理手法です。トップダウン型の意思決定、年功序列、終身雇用を前提とした長期的な人材育成が特徴でした。</p>
        
        <ul>
          <li><strong>指示命令型のリーダーシップ</strong>：上司が明確な指示を出し、部下はそれに従う</li>
          <li><strong>画一的な評価基準</strong>：勤続年数や勤務態度を重視</li>
          <li><strong>集団主義</strong>：個人よりも組織全体の調和を優先</li>
          <li><strong>長時間労働の美徳化</strong>：努力と忍耐が評価される文化</li>
        </ul>
        
        <h3>令和型マネジメントの特徴</h3>
        <p>令和時代のマネジメントは、多様性、柔軟性、個人の自律性を重視します。変化の激しい環境に対応するため、より対話的で協働的なアプローチが求められています。</p>
        
        <ul>
          <li><strong>対話型のリーダーシップ</strong>：1on1ミーティングを通じた双方向のコミュニケーション</li>
          <li><strong>成果主義と個別評価</strong>：個人の強みと成果を重視した評価</li>
          <li><strong>多様性の尊重</strong>：個々の価値観やワークスタイルを認める</li>
          <li><strong>ワークライフバランス</strong>：生産性と働きやすさの両立</li>
          <li><strong>心理的安全性</strong>：失敗を恐れず挑戦できる環境づくり</li>
        </ul>
        
        <h3>移行のポイント</h3>
        <p>昭和型から令和型への移行は一朝一夕にはできません。組織文化の変革には時間がかかりますが、まずは管理職の意識改革から始めることが重要です。部下との対話を増やし、個々の強みを活かすマネジメントを実践していきましょう。</p>
      `
    },
    {
      id: 2,
      category: 'コラム',
      title: 'コーチングとメンタリングの違い',
      excerpt: '似ているようで異なる二つの育成手法。それぞれの特徴と効果的な使い分けについて詳しく解説します。',
      date: '2024年3月20日',
      image: 'https://readdy.ai/api/search-image?query=Bright%20modern%20office%20workspace%20with%20comfortable%20seating%20area%2C%20plants%2C%20natural%20lighting%2C%20open%20collaborative%20space%20design%2C%20whiteboards%20with%20colorful%20sticky%20notes%2C%20creative%20brainstorming%20setup%20without%20people%2C%20inspiring%20work%20environment&width=400&height=250&seq=newsj2&orientation=landscape',
      content: `
        <h3>コーチングとは</h3>
        <p>コーチングは、相手の中にある答えを引き出す対話型の育成手法です。コーチは答えを教えるのではなく、質問を通じて相手の気づきと自発的な行動を促します。</p>
        
        <h4>コーチングの特徴</h4>
        <ul>
          <li><strong>質問中心のアプローチ</strong>：「どう思う？」「どうしたい？」と問いかける</li>
          <li><strong>短期的な目標達成</strong>：具体的な課題解決や目標達成を支援</li>
          <li><strong>対等な関係性</strong>：上下関係ではなく、パートナーとしての関係</li>
          <li><strong>答えは相手の中にある</strong>：相手の自律性と主体性を重視</li>
          <li><strong>構造化されたプロセス</strong>：GROWモデルなどのフレームワークを活用</li>
        </ul>
        
        <h3>メンタリングとは</h3>
        <p>メンタリングは、経験豊富な先輩（メンター）が、後輩（メンティ）に対して知識や経験を伝え、長期的な成長を支援する育成手法です。</p>
        
        <h4>メンタリングの特徴</h4>
        <ul>
          <li><strong>経験の共有</strong>：メンターの知識や経験を積極的に伝える</li>
          <li><strong>長期的な関係性</strong>：キャリア全体を見据えた継続的な支援</li>
          <li><strong>アドバイスと助言</strong>：具体的な方法や選択肢を提示する</li>
          <li><strong>ロールモデル</strong>：メンター自身が手本となる</li>
          <li><strong>包括的なサポート</strong>：仕事だけでなく、キャリアや人生についても相談</li>
        </ul>
        
        <h3>効果的な使い分け</h3>
        <p><strong>コーチングが適している場面：</strong></p>
        <ul>
          <li>部下が既に一定の知識やスキルを持っている場合</li>
          <li>自分で考える力を育てたい場合</li>
          <li>短期的な目標達成や課題解決が必要な場合</li>
          <li>モチベーションを高めたい場合</li>
        </ul>
        
        <p><strong>メンタリングが適している場面：</strong></p>
        <ul>
          <li>新入社員や若手社員の育成</li>
          <li>専門知識や業界知識の伝承が必要な場合</li>
          <li>長期的なキャリア開発を支援したい場合</li>
          <li>組織文化や価値観を伝えたい場合</li>
        </ul>
        
        <h3>両方を組み合わせる</h3>
        <p>実際の育成現場では、コーチングとメンタリングを状況に応じて使い分けることが効果的です。基本的な知識はメンタリングで伝え、その後はコーチングで自律的な成長を促すといった組み合わせが理想的です。</p>
      `
    },
    {
      id: 3,
      category: 'コラム',
      title: '効果的な1on1ミーティングの進め方',
      excerpt: '部下との信頼関係を築き、成長を促進する1on1ミーティングの具体的な手法をご紹介します。',
      date: '2024年3月18日',
      image: 'https://readdy.ai/api/search-image?query=Modern%20office%20meeting%20room%20with%20conference%20table%2C%20notebooks%2C%20pens%2C%20coffee%20cups%2C%20business%20documents%20spread%20on%20table%2C%20natural%20lighting%20through%20windows%2C%20professional%20workspace%20setup%20without%20people%2C%20clean%20minimalist%20design&width=400&height=250&seq=newsj3&orientation=landscape',
      content: `
        <h3>1on1ミーティングの重要性</h3>
        <p>現代の組織運営において、1on1ミーティングは部下の成長と組織の発展に欠かせない要素となっています。定期的な対話を通じて、信頼関係の構築と個人の能力開発を同時に実現できます。</p>
        
        <h3>効果的な進め方のポイント</h3>
        <ul>
          <li><strong>定期的な実施</strong>：月1回以上の頻度で継続的に行う</li>
          <li><strong>安心できる環境</strong>：プライベートな空間で集中できる時間を確保</li>
          <li><strong>傾聴の姿勢</strong>：部下の話を最後まで聞き、共感を示す</li>
          <li><strong>具体的なフィードバック</strong>：行動に基づいた建設的な意見交換</li>
        </ul>
        
        <h3>よくある課題と解決策</h3>
        <p>多くの管理職が直面する「何を話せばよいかわからない」という課題には、事前の準備と構造化されたアジェンダが効果的です。部下の目標設定、現在の課題、今後の成長計画について体系的に話し合うことで、有意義な時間を創出できます。</p>
      `
    },
    {
      id: 4,
      category: 'コラム',
      title: 'チームの心理的安全性を高める方法',
      excerpt: 'Googleが重視する心理的安全性。チームメンバーが安心して発言できる環境づくりのポイントを解説します。',
      date: '2024年3月15日',
      image: 'https://readdy.ai/api/search-image?query=Bright%20modern%20office%20workspace%20with%20comfortable%20seating%20area%2C%20plants%2C%20natural%20lighting%2C%20open%20collaborative%20space%20design%2C%20whiteboards%20with%20colorful%20sticky%20notes%2C%20creative%20brainstorming%20setup%20without%20people%2C%20inspiring%20work%20environment&width=400&height=250&seq=newsj4&orientation=landscape',
      content: `
        <h3>心理的安全性とは</h3>
        <p>心理的安全性とは、チームメンバーが恐れることなく、自分の意見や疑問、ミスについて発言できる環境のことです。Googleの研究により、高いパフォーマンスを発揮するチームの最重要要素として注目されています。</p>
        
        <h3>心理的安全性を高める具体的な方法</h3>
        <ul>
          <li><strong>失敗を学習機会として捉える</strong>：ミスを責めるのではなく、改善のチャンスとして活用</li>
          <li><strong>多様な意見を歓迎する</strong>：異なる視点や反対意見も価値あるものとして受け入れる</li>
          <li><strong>質問しやすい雰囲気づくり</strong>：「わからない」と言える環境を整備</li>
          <li><strong>リーダーが率先して脆弱性を示す</strong>：完璧でない自分を見せることで、他者も安心して発言できる</li>
        </ul>
        
        <h3>測定と改善</h3>
        <p>心理的安全性は定期的なアンケートや1on1ミーティングを通じて測定できます。継続的な改善により、チーム全体のパフォーマンス向上を実現しましょう。</p>
      `
    },
    {
      id: 5,
      category: '研修情報',
      title: '新プログラム「デジタル時代のリーダーシップ研修」を開始',
      excerpt: 'AI・DXが進む現代において求められるリーダーシップスキルを習得する新しい研修プログラムをリリースしました。',
      date: '2024年3月10日',
      image: 'https://readdy.ai/api/search-image?query=Modern%20digital%20technology%20concept%20with%20laptop%20computer%2C%20tablet%2C%20smartphone%2C%20digital%20charts%20and%20graphs%20on%20screen%2C%20futuristic%20office%20desk%20setup%2C%20LED%20lighting%2C%20high-tech%20workspace%20without%20people%2C%20innovation%20theme&width=400&height=250&seq=newsj5&orientation=landscape',
      content: `
        <h3>プログラム概要</h3>
        <p>デジタル変革が加速する現代において、従来のリーダーシップスキルだけでは対応が困難な場面が増えています。本プログラムでは、AI・DX時代に求められる新しいリーダーシップスキルを体系的に学習できます。</p>
        
        <h3>主な学習内容</h3>
        <ul>
          <li><strong>デジタルマインドセット</strong>：変化を恐れず、新技術を活用する思考法</li>
          <li><strong>リモートチームマネジメント</strong>：オンライン環境でのチーム運営手法</li>
          <li><strong>データドリブン意思決定</strong>：データを活用した合理的な判断プロセス</li>
          <li><strong>アジャイルリーダーシップ</strong>：迅速な変化対応と継続的改善</li>
        </ul>
        
        <h3>研修形式</h3>
        <p>2日間の集中研修（オンライン・オフライン選択可）+ 3ヶ月間のフォローアップセッション。実際のケーススタディを用いた実践的なワークショップ形式で進行します。</p>
        
        <h3>対象者</h3>
        <p>中間管理職から経営層まで、デジタル変革をリードする立場の方々を対象としています。</p>
      `
    },
    {
      id: 6,
      category: 'コラム',
      title: '研修効果を最大化する事前・事後の取り組み',
      excerpt: '研修の効果を持続させ、実際の業務に活かすための具体的な方法をご紹介します。',
      date: '2024年3月5日',
      image: 'https://readdy.ai/api/search-image?query=Training%20evaluation%20charts%20and%20graphs%20on%20desk%2C%20performance%20metrics%20dashboard%20on%20computer%20screen%2C%20business%20analytics%20reports%2C%20measuring%20tools%20and%20calculators%2C%20professional%20assessment%20setup%20without%20people%2C%20data%20analysis%20workspace&width=400&height=250&seq=newsj6&orientation=landscape',
      content: `
        <h3>研修効果測定の重要性</h3>
        <p>研修投資の効果を最大化するためには、適切な事前準備と事後フォローが不可欠です。カークパトリックの4段階評価モデルを活用した体系的なアプローチをご紹介します。</p>
        
        <h3>事前準備のポイント</h3>
        <ul>
          <li><strong>明確な目標設定</strong>：研修で達成したい具体的な成果を定義</li>
          <li><strong>現状把握</strong>：参加者のスキルレベルと課題の事前調査</li>
          <li><strong>期待値の共有</strong>：上司と部下間での研修目的の合意形成</li>
          <li><strong>学習環境の整備</strong>：集中して学習できる環境の確保</li>
        </ul>
        
        <h3>事後フォローの実践</h3>
        <ul>
          <li><strong>アクションプランの作成</strong>：学んだ内容を業務に活かす具体的な計画</li>
          <li><strong>定期的な振り返り</strong>：1ヶ月後、3ヶ月後の進捗確認</li>
          <li><strong>上司によるサポート</strong>：実践を促進する環境づくり</li>
          <li><strong>成果の可視化</strong>：行動変容と業績向上の測定</li>
        </ul>
        
        <h3>継続的な改善</h3>
        <p>研修効果の測定結果を次回の研修設計に活かすことで、組織全体の学習効果を継続的に向上させることができます。</p>
      `
    }
  ];

  // Utility function (currently not used but retained from original)
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const filteredNews = activeCategory === '全て' 
    ? newsItems 
    : newsItems.filter(item => item.category === activeCategory);

  const openArticleModal = (article: any) => {
    setSelectedArticle(article);
    setIsModalOpen(true);
  };

  const closeArticleModal = () => {
    setIsModalOpen(false);
    setSelectedArticle(null);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">お知らせ</h1>
              <p className="text-xl text-blue-100">
                最新の研修情報、コラム、メディア掲載情報をお届けします
              </p>
            </div>
          </div>
        </section>

        {/* Category Filter */}
        <section className="py-8 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="flex flex-wrap justify-center gap-4">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`px-6 py-2 rounded-full font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    activeCategory === category
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* News Articles */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredNews.map((item) => (
                  <article key={item.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                    <div className="h-48 bg-gray-200 overflow-hidden">
                      <img
                        src={`https://readdy.ai/api/search-image?query=${encodeURIComponent(
                          item.category === 'コラム' 
                            ? 'Professional business meeting discussion in modern office environment with people collaborating and sharing ideas, clean minimalist background, corporate atmosphere, natural lighting'
                            : item.category === '研修情報'
                            ? 'Corporate training session with professional instructor teaching business skills in modern conference room, people learning and taking notes, clean professional environment'
                            : item.category === 'メディア掲載'
                            ? 'Business newspaper and media coverage with professional documents and reports on clean desk, corporate news and publications, minimalist office setting'
                            : item.category === 'イベント'
                            ? 'Professional business seminar or conference with speakers and audience in modern venue, corporate event atmosphere, clean professional setting'
                            : 'Modern corporate office environment with professional business atmosphere, clean minimalist design, natural lighting and contemporary workspace'
                        )}&width=400&height=240&seq=${item.id}&orientation=landscape`}
                        alt={item.title}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center text-sm text-gray-500 mb-3">
                        <i className="ri-calendar-line mr-2"></i>
                        {item.date}
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3 line-clamp-2">
                        {item.title}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                        {item.excerpt}
                      </p>
                      <button
                        onClick={() => openArticleModal(item)}
                        className="flex items-center text-blue-600 font-medium text-sm hover:text-blue-700 transition-colors cursor-pointer"
                      >
                        詳細を見る
                        <i className="ri-arrow-right-line ml-2"></i>
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Newsletter Section */}
        <section className="py-20 bg-blue-50">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                最新情報をお届けします
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                人材育成に関する最新のコラムや研修情報をメールでお届けします
              </p>
              <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="メールアドレスを入力"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  登録する
                </button>
              </form>
            </div>
          </div>
        </section>
      </main>

      {/* Article Modal */}
      {isModalOpen && selectedArticle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <i className="ri-calendar-line mr-2"></i>
                    {selectedArticle.date}
                    <span className="ml-4 px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-xs font-medium">
                      {selectedArticle.category}
                    </span>
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900">
                    {selectedArticle.title}
                  </h1>
                </div>
                <button
                  onClick={closeArticleModal}
                  className="text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              
              <div 
                className="prose prose-lg max-w-none text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
              />
              
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={closeArticleModal}
                    className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    閉じる
                  </button>
                  <a
                    href="/contact"
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap text-center"
                  >
                    お問い合わせ
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default NewsPage;
